/**
 * libav-remux.js — window.LibavRemux
 *
 * Memory-bounded streaming remux for LARGE files, via a self-hosted libav.js
 * (webcodecs variant, /vendor/libav/). Used by the universal video player when
 * whole-file FFmpeg-WASM remux would blow the ~2 GB WASM heap (movies over
 * ~1.5 GB): libav reads the File on demand (readahead device — only slices ever
 * materialize) and writes the remuxed MP4 out incrementally to an OPFS file
 * (streaming writer device), so neither end holds the whole movie in memory.
 * The result is a normal MP4 played natively by <video> (browser HW decode +
 * A/V sync), removing the size ceiling entirely.
 *
 *   const { url, blob, cleanup } = await LibavRemux.remuxToPlayable(file, {
 *     onProgress: p => {},          // 0..1
 *     signal,                       // optional AbortSignal
 *   });
 *   // ... player.load(url) ...   then later: cleanup();
 *
 * Matroska (and some others) store only PTS; the mp4 muxer needs monotonic DTS,
 * so DTS is reconstructed per stream in decode order. Never throws silently —
 * rejects with a typed error the caller localizes.
 *
 * Requires: /vendor/libav/libav-webcodecs.js (+ glue + wasm). LGPL — see the
 * vendored README.txt.
 */
(function () {
'use strict';
if (typeof window === 'undefined' || window.LibavRemux) return;

var VENDOR = '/vendor/libav/';
var NOPTS_HI = -2147483648;
var _libav = null, _loading = null;

function _injectLoader() {
    return new Promise(function (resolve, reject) {
        if (window.LibAV && typeof window.LibAV.LibAV === 'function') return resolve();
        var existing = document.querySelector('script[data-libav-loader]');
        if (existing) {
            existing.addEventListener('load', function () { resolve(); });
            existing.addEventListener('error', function () { reject(new Error('libav loader failed')); });
            if (window.LibAV) resolve();
            return;
        }
        var s = document.createElement('script');
        s.src = VENDOR + 'libav-webcodecs.js';
        s.async = true;
        s.setAttribute('data-libav-loader', '1');
        // Under strict-dynamic CSP, a script injected by an already-trusted
        // (nonce'd) script inherits trust — no nonce attribute needed here.
        s.onload = function () { resolve(); };
        s.onerror = function () { reject(new Error('libav loader failed')); };
        document.head.appendChild(s);
    });
}

// Lazy singleton. Worker mode (off-main-thread) preferred; falls back to
// main-thread if the worker can't spin up.
async function _getLibav() {
    if (_libav) return _libav;
    if (_loading) return _loading;
    _loading = (async function () {
        await _injectLoader();
        if (!window.LibAV) throw new Error('libav unavailable');
        window.LibAV.base = VENDOR.replace(/\/+$/, '');
        var libav;
        try { libav = await window.LibAV.LibAV(); }
        catch (e) { libav = await window.LibAV.LibAV({ noworker: true }); }
        _libav = libav;
        return libav;
    })();
    try { return await _loading; }
    catch (e) { _loading = null; throw e; }
}

// Whether this browser can do the streaming remux at all.
function isSupported() {
    return typeof WebAssembly !== 'undefined' &&
           !!(navigator.storage && navigator.storage.getDirectory);
}

async function _opfsWriter(name) {
    if (!(navigator.storage && navigator.storage.getDirectory)) return null;
    try {
        var dir = await navigator.storage.getDirectory();
        try { await dir.removeEntry(name); } catch (_) {}
        var fh = await dir.getFileHandle(name, { create: true });
        var ws = await fh.createWritable({ keepExistingData: false });
        return { dir: dir, fh: fh, ws: ws, name: name };
    } catch (_) { return null; }
}

/**
 * Stream-remux `file` (any container libav can demux + browser-decodable codec)
 * into a playable MP4. Returns { url, blob, cleanup }.
 */
async function remuxToPlayable(file, opts) {
    opts = opts || {};
    var onProgress = opts.onProgress || function () {};
    var signal = opts.signal;
    var libav = await _getLibav();

    // The owner tag: a movie-sized file the next page load removes once this
    // page is gone (window.__opfsOwner, timbrica-core.js) — nothing else did.
    var opfs = await _opfsWriter('vp_remux_' + Date.now() + ((window.__opfsOwner && window.__opfsOwner.tag('_')) || '') + '.mp4');
    var memChunks = opfs ? null : [];
    var writeChain = Promise.resolve();
    var maxEnd = 0;
    var srcSize = file.size || 1;

    libav.onwrite = function (name, pos, buf) {
        var data = buf.slice(0);           // buf may be reused by libav — copy
        maxEnd = Math.max(maxEnd, pos + data.length);
        // remux output ≈ input size, so bytes-out/size is a fair progress proxy
        try { onProgress(Math.max(0, Math.min(0.99, maxEnd / srcSize))); } catch (_) {}
        if (opfs) {
            writeChain = writeChain.then(function () {
                return opfs.ws.write({ type: 'write', position: pos, data: data });
            }).catch(function () {});
        } else {
            memChunks.push({ pos: pos, data: data });
        }
    };

    var ifc = 0, ofc = 0, cleaned = false;
    async function teardown() {
        if (cleaned) return; cleaned = true;
        try { libav.onwrite = null; } catch (_) {}
        try { await libav.unlink('in'); } catch (_) {}
    }

    try {
        // STREAMING INPUT: readahead device reads the File on demand.
        await libav.mkreadaheadfile('in', file);
        var demux = await libav.ff_init_demuxer_file('in');
        ifc = demux[0];
        var istreams = demux[1];
        if (!istreams || !istreams.length) throw new Error('no streams');
        var pkt = await libav.av_packet_alloc();

        // STREAMING OUTPUT: device:true → ff_init_muxer creates a writer device;
        // onwrite (set above) streams the fMP4 out. codecpars:true = the stream
        // specs are AVCodecParameters (from the demuxer), not codec contexts.
        var mux = await libav.ff_init_muxer(
            { filename: 'out.mp4', open: true, codecpars: true, device: true },
            istreams.map(function (s) { return [s.codecpar, s.time_base_num, s.time_base_den]; })
        );
        ofc = mux[0];
        await libav.avformat_write_header(ofc, 0);

        var dtsCtr = {}, lastDtsW = {}, lastDur = {};
        var loops = 0, emptyRuns = 0;
        while (true) {
            if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
            var read = await libav.ff_read_frame_multi(ifc, pkt, { limit: 8 * 1024 * 1024 });
            var res = read[0], packets = read[1], got = 0;
            for (var idx in packets) {
                var arr = packets[idx];
                for (var i = 0; i < arr.length; i++) {
                    var p = arr[i];
                    if (p.dtshi === NOPTS_HI) {           // container had no DTS → reconstruct
                        var si = p.stream_index;
                        if (dtsCtr[si] === undefined) dtsCtr[si] = 0;
                        var d = dtsCtr[si];
                        if (lastDtsW[si] !== undefined && d <= lastDtsW[si]) d = lastDtsW[si] + 1;
                        p.dts = d; p.dtshi = 0;
                        if (p.ptshi === NOPTS_HI) { p.pts = d; p.ptshi = 0; }
                        lastDtsW[si] = d;
                        var dur = (p.duration > 0) ? p.duration : (lastDur[si] || 1);
                        lastDur[si] = dur; dtsCtr[si] = d + dur;
                    }
                }
                if (arr.length) { await libav.ff_write_multi(ofc, pkt, arr); got += arr.length; }
            }
            // EOF → done. Otherwise (incl. EAGAIN = byte-limit reached, more data
            // remains) keep reading the next batch. Guard against a stuck stream.
            if (res === libav.AVERROR_EOF || res === -541478725) break;
            if (got === 0) { if (++emptyRuns > 300) break; } else { emptyRuns = 0; }
            if (++loops > 2000000) break;
        }
        await libav.av_write_trailer(ofc);
        try { await libav.ff_free_muxer(ofc, mux[2]); } catch (_) {}
        try { await libav.avformat_close_input_js(ifc); } catch (_) {}

        onProgress(1);

        var blob, url;
        if (opfs) {
            await writeChain;
            try { await opfs.ws.close(); } catch (_) {}
            var outFile = await opfs.fh.getFile();     // disk-backed — no heap copy
            url = URL.createObjectURL(outFile);
            blob = outFile;
        } else {
            var size = 0;
            for (var c = 0; c < memChunks.length; c++) size = Math.max(size, memChunks[c].pos + memChunks[c].data.length);
            var outBuf = new Uint8Array(size);
            for (var k = 0; k < memChunks.length; k++) outBuf.set(memChunks[k].data, memChunks[k].pos);
            blob = new Blob([outBuf], { type: 'video/mp4' });
            url = URL.createObjectURL(blob);
        }
        await teardown();

        return {
            url: url, blob: blob,
            cleanup: function () {
                try { URL.revokeObjectURL(url); } catch (_) {}
                if (opfs) { try { opfs.dir.removeEntry(opfs.name); } catch (_) {} }
            }
        };
    } catch (err) {
        await teardown();
        if (opfs) { try { await opfs.ws.close(); } catch (_) {} try { await opfs.dir.removeEntry(opfs.name); } catch (_) {} }
        throw err;
    }
}

/* ── remuxFile: the container step for a finished multi-GB result ─────────────
 * The same copy remux, with the whole loop in ONE dedicated worker
 * (libav-remux-worker.js): input read in 8 MB windows, output written to OPFS
 * through a synchronous access handle. Neither end is in memory, so the size of
 * the file is not a term at all — unlike FFmpeg-WASM's `-c copy`, whose output
 * sits in MEMFS (a 3.3 GB /enhance-video result died there after 27 minutes of
 * encoding, request #1848). Measured 08.10.2026 — see the worker's header.
 *
 *   const r = await LibavRemux.remuxFile(file, { format: 'mkv', signal, onProgress });
 *   r.file  — OPFS-backed File; r.cleanup() removes it (the owner sweep does too).
 *   fflags  — optional AVFMT_FLAG_* bits OR-ed into the demuxer (GENPTS 0x4,
 *             DISCARD_CORRUPT 0x100): the `-fflags` of a repair copy (/repair-video).
 *   select  — optional {audio:'all'|'none', audioKeep:[n…], hvc1:bool}: the first
 *             real video stream + the audio asked for, nothing else (/video-converter);
 *             without it every stream goes out (the container step).
 *
 * Rejects with err.code: 'UNSUPPORTED' (no Worker / no sync access handle, or a
 * format the build cannot write — libav here writes mp4, mov and mkv, not avi),
 * 'NO_ROOM' (the disk / OPFS quota refused a write — the copy is NOT finished),
 * 'STALLED' (no word from the worker for 120 s of visible time), 'REMUX_FAILED';
 * a cancel is the AbortError of the caller's signal. */
var _remuxSeq = 0, _remuxBroken = '';
/* The sync access handle exists only inside a dedicated worker, so the page
   cannot see it: the worker answers for itself (an error on its first message),
   and a browser that refused once is not asked again on this page. */
function canRemuxFile() {
    if (_remuxBroken) return false;
    try {
        return typeof Worker !== 'undefined' && typeof WebAssembly !== 'undefined' &&
            !!(navigator.storage && navigator.storage.getDirectory);
    } catch (_) { return false; }
}
function _typed(code, msg) { var e = new Error('libav-remux: ' + msg); e.code = code; return e; }

async function remuxFile(file, opts) {
    opts = opts || {};
    var fmt = String(opts.format || 'mp4').toLowerCase();
    if (!/^(mp4|mov|mkv)$/.test(fmt)) throw _typed('UNSUPPORTED', 'format ' + fmt);
    if (!canRemuxFile()) throw _typed('UNSUPPORTED', 'no worker or sync access handle');
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : function () {};
    var signal = opts.signal;
    if (signal && signal.aborted) throw new DOMException('cancelled', 'AbortError');

    var src = function (p) { return typeof window.__jsSrc === 'function' ? window.__jsSrc(p) : p; };
    var abs = function (p) { return new URL(p, location.href).href; };
    var cfg = {
        loader: abs(src(VENDOR + 'libav-webcodecs.js')),
        base: abs(VENDOR.replace(/\/+$/, '')),
        wasm: abs(VENDOR + 'libav-6.9.8.1-webcodecs.wasm.wasm')
    };
    /* `vp_remux_<ms>` is the 'remux' kind of the OPFS owner sweep
       (window.__opfsOwner, timbrica-core.js): the file goes with its page. */
    var name = 'vp_remux_' + Date.now() + '_' + (++_remuxSeq) + ((window.__opfsOwner && window.__opfsOwner.tag('_')) || '') + '.' + fmt;
    var dir = await navigator.storage.getDirectory();
    var drop = function () { try { dir.removeEntry(name).catch(function () {}); } catch (_) {} };
    var w = new Worker(src('/js/libav-remux-worker.js'));
    var clock = function () { return typeof window.__visibleNow === 'function' ? window.__visibleNow() : performance.now(); };

    return new Promise(function (resolve, reject) {
        var heard = clock(), settled = false;
        var finish = function (fn, val) {
            if (settled) return; settled = true;
            clearInterval(dog);
            if (signal) try { signal.removeEventListener('abort', onAbort); } catch (_) {}
            try { w.terminate(); } catch (_) {}       // the wasm heap goes with the worker
            fn(val);
        };
        var onAbort = function () { drop(); finish(reject, new DOMException('cancelled', 'AbortError')); };
        if (signal) signal.addEventListener('abort', onAbort);
        var dog = setInterval(function () {
            if (clock() - heard > 120000) { drop(); finish(reject, _typed('STALLED', 'no progress for 120 s')); }
        }, 5000);
        w.onmessage = function (ev) {
            var d = ev.data || {};
            heard = clock();
            if (d.type === 'progress') { try { onProgress(Math.max(0, Math.min(0.99, +d.p || 0))); } catch (_) {} return; }
            if (d.type === 'error') {
                drop();
                if (d.code === 'UNSUPPORTED') _remuxBroken = d.message || 'unsupported';
                finish(reject, _typed(d.code === 'UNSUPPORTED' ? 'UNSUPPORTED' : d.code === 'NO_ROOM' ? 'NO_ROOM' : 'REMUX_FAILED', d.message || 'worker error'));
                return;
            }
            if (d.type === 'done') {
                dir.getFileHandle(name).then(function (fh) { return fh.getFile(); }).then(function (f) {
                    try { onProgress(1); } catch (_) {}
                    finish(resolve, { file: f, name: name, size: f.size, ms: d.ms, reads: d.reads, packets: d.packets, cleanup: drop });
                }, function (e) { drop(); finish(reject, _typed('REMUX_FAILED', 'output unreadable: ' + ((e && e.message) || e))); });
            }
        };
        w.onerror = function (ev) {
            drop();
            finish(reject, _typed('REMUX_FAILED', 'worker: ' + ((ev && ev.message) || 'failed to start')));
        };
        w.postMessage({ type: 'remux', id: name, file: file, format: fmt, outName: name, cfg: cfg, select: opts.select || null, fflags: opts.fflags >>> 0 || 0 });
    });
}

window.LibavRemux = {
    remuxToPlayable: remuxToPlayable,
    remuxFile: remuxFile,
    canRemuxFile: canRemuxFile,
    isSupported: isSupported,
    _getLibav: _getLibav,        // exposed for tests
    __isStub: false
};
})();
